import { neutral } from '@bloomfieldrobotics/canopy';
import { Box, SxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGrid,
  DataGridProps,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridCsvExportOptions,
  GridRowSelectionModel,
  GridSlotsComponent,
  useGridApiRef,
} from '@mui/x-data-grid';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useState } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { formatNumberWithCommas } from './utils/formatNumberWithCommas';
import { hideColumnTogglesForHiddenColumns } from './utils/hideColumnTogglesForHiddenColumns';

export const MISSING_DATA_PLACEHOLDER = '--';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  width: '100%',
  border: 'none',
  borderRadius: 0,
  backgroundColor: theme.vars.palette.background.paper,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: neutral[750],
    borderRadius: 0,
    border: 'none',
    borderBottom: `0.5px solid ${neutral[800]}`,
    color: theme.vars.palette.text.secondary,
    '& .MuiDataGrid-sortIcon': {
      color: theme.vars.palette.text.secondary,
    },
    '& .MuiDataGrid-menuIcon': {
      '& .MuiIconButton-root': {
        color: theme.vars.palette.text.secondary,
      },
    },
  },
  '& .MuiDataGrid-withBorderColor': {
    borderColor: neutral[800],
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader--sorted': {
    color: theme.vars.palette.text.primary,
  },
  '& .MuiDataGrid-iconSeparator': {
    display: 'none',
  },
  '& .noDataPlaceholder': {
    color: theme.vars.palette.text.disabled,
  },
}));

const globalColumnDef: Partial<GridColDef> = {
  headerAlign: 'left',
  align: 'left',
  flex: 1,
  minWidth: 50,
  valueFormatter: (value) => {
    if (typeof value === 'number') {
      return formatNumberWithCommas(value);
    }
    return value != null ? value : MISSING_DATA_PLACEHOLDER;
  },
};

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onDownloadClick: () => void;
  }
}

type Props = Pick<
  DataGridProps,
  | 'initialState'
  | 'pageSizeOptions'
  | 'rows'
  | 'columns'
  | 'loading'
  | 'checkboxSelection'
  | 'disableRowSelectionOnClick'
  | 'getRowId'
  | 'apiRef'
> & {
  toolbar?: GridSlotsComponent['toolbar'];
  noRowsOverlay?: GridSlotsComponent['noRowsOverlay'];
  hiddenColumns?: string[];
  csvOptions?: GridCsvExportOptions;
  selectedRowCountDispatchFunction?: ActionCreatorWithPayload<number | null>;
  /**
   * Whether to hide the pagination controls.
   * Use at your own discretion. This is not recommended for large datasets,
   * or  in general. I am doing a hack here since we don't have the
   * paid version of this UI to disable pagination.
   */
  forceHidePagination?: boolean;
  sx?: SxProps;
};

/**
 * A generic data grid component that displays rows and columns of data with optional features such as checkbox selection and custom toolbar headers.
 *
 * @component
 * @param {Props} props - The properties for the GenericDataGrid component.
 * @param {Array} rows - The data rows to be displayed in the data grid.
 * @param {Array} columns - The column definitions for the data grid.
 * @param {booleans} [loading] - Whether the data grid is in a loading state.
 * @param {boolean} [checkboxSelection=false] - Whether to enable checkbox selection for rows.
 * @param {boolean} [disableRowSelectionOnClick=false] - Whether to disable row selection when clicking on a row.
 * @param {React.ReactNode} [toolbarHeader] - An optional custom toolbar header to be displayed above the data grid.
 * @param {Function} [selectedRowCountDispatchFunction] - An optional function to dispatch the number of selected rows to Redux for this table specifically since multiple tables can be displayed at once, so independent tracking is necessary.
 * @param {React.ReactNode} [noRowsOverlay] - An optional custom overlay to display when there are no rows in the data grid.
 *@param {Array} [hiddenColumns] - The columns to be hidden from the user.  If not provided, all columns will be shown.
 * @param {GridCsvExportOptions} [csvOptions] - The options for exporting the data grid to CSV.
 *
 * @returns {JSX.Element} The rendered GenericDataGrid component.
 */
const GenericDataGrid: React.FC<Props> = ({
  rows,
  columns,
  toolbar,
  noRowsOverlay,
  hiddenColumns,
  csvOptions,
  selectedRowCountDispatchFunction,
  forceHidePagination: hidePagination,
  disableRowSelectionOnClick,
  sx,
  ...dataGridPassthroughProps
}) => {
  const dispatch = useAppDispatch();
  const apiRef = useGridApiRef();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(
      hiddenColumns
        ? hiddenColumns.reduce((acc, curr) => ({ ...acc, [curr]: false }), {})
        : {}
    );

  // This function is called any time the number of selected rows changes, and it dispatches the number of selected rows to the Redux store.  Since multiple tables can be on the same page, this function allows each table to dispatch the number of selected rows to the Redux store independently.
  const handleSelectionModelChange = (
    selectionModel: GridRowSelectionModel
  ) => {
    if (selectedRowCountDispatchFunction) {
      dispatch(selectedRowCountDispatchFunction(selectionModel.length));
    }
  };

  const onDownloadClick = () => {
    try {
      apiRef.current.exportDataAsCsv(csvOptions);
    } catch (e) {
      console.error('Error exporting data to CSV: ', e);
      alert('Error exporting data, please try again.');
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        minWidth: '50px',
        overflowX: 'auto',
        overflowY: 'hidden',
        bgcolor: neutral[800],
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <StyledDataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns.map((col) => {
          // This is a temp hack so that the globalColDef valueFormatter doesn't override ones
          // provided by columns themselves. This is a workaround until we have time
          // to define robust value formatters for basic column types.
          const { valueFormatter: colDefValueFormatter, ...restOfColDef } = col;
          const {
            valueFormatter: globalValueFormatter,
            ...restOfGlobalColDef
          } = globalColumnDef;

          return {
            ...restOfColDef,
            ...restOfGlobalColDef,
            valueFormatter: colDefValueFormatter ?? globalValueFormatter,
          };
        })}
        columnVisibilityModel={columnVisibilityModel}
        onRowSelectionModelChange={handleSelectionModelChange}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        slots={{
          toolbar,
          noRowsOverlay: noRowsOverlay,
          pagination: hidePagination ? null : undefined,
        }}
        getCellClassName={(params: GridCellParams) =>
          params.value === null ||
          params.value === undefined ||
          params.value === MISSING_DATA_PLACEHOLDER
            ? 'noDataPlaceholder'
            : ''
        }
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            onDownloadClick: onDownloadClick,
          },
          columnsManagement: {
            getTogglableColumns: hiddenColumns
              ? (columns) =>
                  hideColumnTogglesForHiddenColumns(columns, hiddenColumns)
              : undefined,
          },
        }}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        sx={
          {
            ...(disableRowSelectionOnClick
              ? {
                  '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus ':
                    {
                      outline: 'none', // hide clicked cell outline
                    },
                }
              : {}),
            ...(hidePagination
              ? {
                  '& .MuiDataGrid-footerContainer': {
                    border: 'none',
                    display: 'none',
                  },
                }
              : {}),
            ...sx,
          } as SxProps
        }
        {...dataGridPassthroughProps}
      />
    </Box>
  );
};

export default GenericDataGrid;
